import { useCallback } from "react";
import { useRecoilValue } from "recoil";
import { LanguageCode, ENGLISH_ABBREV, SPANISH_ABBREV } from "@bto/shared";
import { getUserAuth } from "../../recoil/selectors";
import { ReportDownloadRequest } from "../../components/DetailedResults/Services/DetailedResultsService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../analyticsEvents";
import {
  CodeResultSerializer,
  FETAL_SEX_CODE_RESULT,
  FetalSexCodeResultSerializer,
  STANDALONE_FETAL_SEX,
  StandaloneFetalSex,
} from "../../recoil/types";

export type ReportType = "aneuploidy" | "carrier" | "cfc";
export type DownloadLocation = "dashboard" | "report_page";

const REPORTED_FETAL_SEX_VALUES: ReadonlySet<
  FetalSexCodeResultSerializer["value"]
> = new Set([
  FETAL_SEX_CODE_RESULT.MALE,
  FETAL_SEX_CODE_RESULT.FEMALE,
  FETAL_SEX_CODE_RESULT.MALE_MALE,
  FETAL_SEX_CODE_RESULT.FEMALE_FEMALE,
  FETAL_SEX_CODE_RESULT.MALE_FEMALE,
  FETAL_SEX_CODE_RESULT.ONE_OR_BOTH_MALES,
]);

/**
 * `main`'s download-confirmation predicate, kept verbatim for the rollout-off
 * path. It deliberately defaults to warning: any Final or Corrected report that
 * was not opted out or N/A may disclose fetal sex in its PDF, including one
 * whose structured value is missing or stale. Narrowing this to an allowlist
 * would silently drop the confirmation for production patients, who are the
 * majority during a percentage rollout.
 */
const shouldShowLegacyAneuploidyFetalSexWarning = (
  fetalSex: FetalSexCodeResultSerializer | undefined,
): boolean => {
  if (!fetalSex) return false;
  const { status } = fetalSex;
  const value = fetalSex.value?.toLowerCase();
  return (
    (status === "Final" || status === "Corrected") &&
    value !== "not ordered" &&
    value !== "n/a"
  );
};

/**
 * Determines if the fetal sex confirmation modal should be shown for aneuploidy
 * reports.
 *
 * With the standalone rollout off this must reproduce `main` exactly, so that
 * the rollout's control arm matches production. With it on, a HIDDEN standalone
 * result is the report-correction signal (the corrected PDF can contain fetal
 * sex even when structured fetal-sex data is absent or stale) and other non-sex
 * outcomes such as TNP or No Call must not show a disclosure warning.
 *
 * `isStandaloneFetalSexEnabled` defaults to false so an un-updated caller falls
 * back to the broader legacy warning rather than silently suppressing it.
 */
export const shouldShowAneuploidyFetalSexWarning = (
  fetalSex: FetalSexCodeResultSerializer | undefined,
  standaloneFetalSex?: StandaloneFetalSex,
  isStandaloneFetalSexEnabled = false,
): boolean => {
  if (!isStandaloneFetalSexEnabled) {
    return shouldShowLegacyAneuploidyFetalSexWarning(fetalSex);
  }
  if (standaloneFetalSex === STANDALONE_FETAL_SEX.HIDDEN) return true;
  if (!fetalSex) return false;

  const { status } = fetalSex;
  const { value } = fetalSex;

  return (
    (status === "Final" || status === "Corrected") &&
    REPORTED_FETAL_SEX_VALUES.has(value)
  );
};

/**
 * Determines if the fetal sex confirmation modal should be shown for carrier reports.
 * Some Fragile X carrier reports can report fetal sex, so we check if a value exists.
 */
export const shouldShowCarrierFetalSexWarning = (
  fetalSex: CodeResultSerializer | undefined,
): boolean => Boolean(fetalSex?.value);

/**
 * Hook that provides functions to download reports.
 * Handles authentication, API calls, and file download.
 */
export const useReportDownload = () => {
  const auth = useRecoilValue(getUserAuth);

  /**
   * Fetches the PDF URL for a report.
   */
  const getReportUrl = useCallback(
    async (
      reportId: string | undefined,
      reportType: ReportType,
      language?: LanguageCode,
    ): Promise<string | undefined> => {
      if (!reportId) return undefined;

      const response = await ReportDownloadRequest(
        reportId,
        reportType,
        auth?.access_token,
        language,
      );
      if (!response?.ok) {
        throw new Error(
          `Failed to fetch report: ${response?.status ?? "no response"}`,
        );
      }
      const data = await response.json();
      return data.pdf_url as string;
    },
    [auth?.access_token],
  );

  /**
   * Downloads a report as a PDF file.
   */
  const downloadReport = useCallback(
    async (
      reportId: string | undefined,
      reportType: ReportType,
      language?: LanguageCode,
      availableLanguages?: string[],
      downloadLocation: DownloadLocation = "report_page",
    ): Promise<void> => {
      const url = await getReportUrl(reportId, reportType, language);
      if (url) {
        const effectiveLanguage = language ?? ENGLISH_ABBREV;

        // Fire (and await) the analytics event BEFORE triggering the
        // download so the request both starts and completes before
        // `anchor.click()` can cause any navigation that would abort it.
        //
        // Race the await against a 2s timeout so a slow/hung analytics
        // backend can never block the actual download. The underlying
        // fetch keeps running in the background after the race resolves,
        // so the event may still land on the server — we just stop
        // waiting for it. Analytics is non-critical; the download isn't.
        if (auth?.user_id) {
          await Promise.race([
            TrackAnalyticsEvent(
              AnalyticsEventNames.MultiLanguageReportDownloaded,
              auth.access_token,
              auth.user_id,
              {
                report_type: reportType,
                language: effectiveLanguage,
                available_languages: (
                  availableLanguages ?? [ENGLISH_ABBREV]
                ).join(","),
                location: downloadLocation,
                portal_type: "patient",
              },
            ).catch(() => undefined),
            new Promise((resolve) => setTimeout(resolve, 2000)),
          ]);
        }

        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download =
          effectiveLanguage === SPANISH_ABBREV ? "report_es.pdf" : "report.pdf";
        anchor.click();
        anchor.remove();
      }
    },
    [getReportUrl, auth?.user_id, auth?.access_token],
  );

  return { getReportUrl, downloadReport };
};
